/**
 * Customer Chat — UITree types + dependency-free validator (M0 reference, v1).
 *
 * NO external dependencies (no zod). The SAME file is copied into both runtimes:
 *  - the Node BFF (M1) uses `parseUITree` to validate the model's output before returning it;
 *  - the Angular client (M3/M4) uses `parseUITree`/`isUITree` to fail safe before rendering.
 *
 * Keep structurally identical to `Docs/customer-chat/reference/uitree.types.ts` (CONTRACT.md is authoritative).
 */

export const UITREE_VERSION = 1 as const;

/** Validation caps — must match CONTRACT.md §2. */
export const UITREE_MAX_NODES = 200;
export const UITREE_MAX_DEPTH = 10;

export type Gap = 'sm' | 'md' | 'lg';
export type Align = 'left' | 'right' | 'center';
export type Trend = 'up' | 'down' | 'flat';

export interface StackNode {
  type: 'stack';
  direction: 'vertical' | 'horizontal';
  gap?: Gap;
  children: UINode[];
}

export interface GridNode {
  type: 'grid';
  columns: number; // 1–6
  gap?: Gap;
  children: UINode[];
}

export interface HeadingNode {
  type: 'heading';
  level: 1 | 2 | 3 | 4;
  text: string;
}

export interface TextNode {
  type: 'text';
  text: string;
}

export interface MetricNode {
  type: 'metric';
  label: string;
  value: string | number;
  unit?: string;
  trend?: Trend;
}

export interface StatGridNode {
  type: 'statGrid';
  items: { label: string; value: string | number; unit?: string }[];
}

export interface TableColumn {
  key: string;
  label: string;
  align?: Align;
}

export type TableCell = string | number | boolean | null;

export interface TableNode {
  type: 'table';
  columns: TableColumn[];
  /** Inline rows. Omit when the node is data-bound (`dataSource`); the renderer fills rows from data. */
  rows?: Record<string, TableCell>[];
  /** Opt-in target for a sibling `filterBar` sharing this key — local, client-side row filtering only. */
  filterKey?: string;
  /** Data-binding (M7.5 Phase 2): rows come from `data[dataSource]` resolved by `dataPath`. */
  dataSource?: string;
  dataPath?: string;
}

export interface ChartSeries {
  label: string;
  points: { x: string | number; y: number }[];
}

/** Binds one chart series to a column of the resolved data array (M7.5 Phase 2). */
export interface ChartSeriesBinding {
  label: string;
  yKey: string;
}

export interface ChartNode {
  type: 'barChart' | 'lineChart';
  /** Inline series. Omit when data-bound (`dataSource`); the renderer builds series from data. */
  series?: ChartSeries[];
  xLabel?: string;
  yLabel?: string;
  /**
   * Data-binding (M7.5 Phase 2): build series from a tool-result array so an in-place refresh swaps
   * only `data`. `dataPath` resolves an array of rows in `data[dataSource]`; each row yields a point
   * with `x = row[xKey]` and, per `seriesKeys` entry, `y = row[yKey]`.
   */
  dataSource?: string;
  dataPath?: string;
  xKey?: string;
  seriesKeys?: ChartSeriesBinding[];
}

export interface ListNode {
  type: 'list';
  items: { title: string; subtitle?: string; value?: string | number }[];
  /** Opt-in target for a sibling `filterBar` sharing this key — local, client-side item filtering only. */
  filterKey?: string;
}

// ── Interactive nodes (M7) ─────────────────────────────────────────────────────
// Each carries an `action` string + optional `params`; the renderer dispatches them either as a new
// chat turn (button/form/periodNav/toggle, round-trip via /api/chat) or as local renderer state
// (filterBar — never reaches the server). `params` is an opaque bag echoed back to the model.

/**
 * How an action updates the transcript (M7.5). `replace` swaps the originating `assistant-ui` view
 * in place (navigate within a view); `append` adds a new view below (drill down to a new view).
 * Omitted → the renderer's per-node default applies: periodNav/toggle → `replace`, button/form →
 * `append`.
 */
export type ActionTarget = 'replace' | 'append';

export interface ButtonNode {
  type: 'button';
  label: string;
  action: string;
  params?: Record<string, unknown>;
  /** Override the dispatch target (default `append` for a button). */
  target?: ActionTarget;
}

export type FormFieldType = 'text' | 'select';

export interface FormFieldOption {
  label: string;
  value: string;
}

export interface FormField {
  name: string;
  label: string;
  type: FormFieldType;
  /** Required for `select` fields; ignored otherwise. */
  options?: FormFieldOption[];
}

export interface FormNode {
  type: 'form';
  fields: FormField[];
  submitAction: string;
  submitLabel?: string;
  /** Override the dispatch target (default `append` for a form). */
  target?: ActionTarget;
}

export interface FilterBarNode {
  type: 'filterBar';
  /** Sibling `table`/`list` nodes carrying the same key are filtered as the user types. Local only. */
  filterKey: string;
  placeholder?: string;
}

export interface PeriodNavNode {
  type: 'periodNav';
  label: string;
  prevAction: string;
  nextAction: string;
  /**
   * All parameters needed to fetch the previous period — period identifier plus any other context
   * (address IDs, contract IDs, filters) required to re-run the exact same query one period back.
   * The renderer sends `[Action: prevAction] <prevParams>` so navigation chains indefinitely.
   * Updated client-side after each fast-path refresh via {@link updatePeriodNavInTree}.
   */
  prevParams?: Record<string, unknown>;
  /** Same as `prevParams` but for the next period. */
  nextParams?: Record<string, unknown>;
  /** Override the dispatch target (default `replace` — periodNav navigates in place). */
  target?: ActionTarget;
}

export interface ToggleNode {
  type: 'toggle';
  label: string;
  /** Param key set to the checked boolean when dispatched. */
  name: string;
  action: string;
  params?: Record<string, unknown>;
  /** Override the dispatch target (default `replace` — toggle updates the view in place). */
  target?: ActionTarget;
}

/**
 * A file download link (M8). Unlike `button`, clicking it never round-trips through the chat
 * backend — the renderer performs an authenticated fetch of `url` directly against the Klantportaal
 * API origin (the same-origin auth interceptor attaches the bearer token) and saves the response as
 * a file. `url` must already be a fully-formed, absolute API URL (as returned by e.g. the
 * `get_invoice_download_link` MCP tool); the renderer refuses to fetch a URL outside the API origin.
 */
export interface DownloadNode {
  type: 'download';
  label: string;
  url: string;
}

/** The complete v1 allow-list. Anything else renders as `unsupported` on the client. */
export type UINode =
  | StackNode
  | GridNode
  | HeadingNode
  | TextNode
  | MetricNode
  | StatGridNode
  | TableNode
  | ChartNode
  | ListNode
  | ButtonNode
  | FormNode
  | FilterBarNode
  | PeriodNavNode
  | ToggleNode
  | DownloadNode;

export type UINodeType = UINode['type'];

export interface UITree {
  version: typeof UITREE_VERSION;
  root: UINode;
}

export const SUPPORTED_NODE_TYPES: readonly UINodeType[] = [
  'stack',
  'grid',
  'heading',
  'text',
  'metric',
  'statGrid',
  'table',
  'barChart',
  'lineChart',
  'list',
  'button',
  'form',
  'filterBar',
  'periodNav',
  'toggle',
  'download',
] as const;

// ── dependency-free validation ────────────────────────────────────────────────

export type ValidationResult = { ok: true; tree: UITree } | { ok: false; error: string };

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
function isString(v: unknown): v is string {
  return typeof v === 'string';
}
function isNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}
function isScalar(v: unknown): v is string | number {
  return isString(v) || isNumber(v);
}
function isOptString(v: unknown): boolean {
  return v === undefined || isString(v);
}
function isOptRecord(v: unknown): boolean {
  return v === undefined || isRecord(v);
}
function isOptTarget(v: unknown): boolean {
  return v === undefined || oneOf(v, ['replace', 'append'] as const);
}
function oneOf<T extends string>(v: unknown, allowed: readonly T[]): v is T {
  return isString(v) && (allowed as readonly string[]).includes(v);
}

/**
 * Recursively validates an untrusted value against the v1 contract, enforcing the node allow-list,
 * required field types, grid column range, and the node-count / depth caps. Returns a discriminated
 * result with a human-readable error path (no payload data) on failure.
 */
export function validateUITree(value: unknown): ValidationResult {
  if (!isRecord(value)) {
    return { ok: false, error: 'root: expected an object' };
  }
  if (value['version'] !== UITREE_VERSION) {
    return { ok: false, error: `version: expected ${UITREE_VERSION}` };
  }

  let count = 0;

  const node = (n: unknown, depth: number, path: string): string | null => {
    if (depth > UITREE_MAX_DEPTH) {
      return `${path}: exceeds max depth ${UITREE_MAX_DEPTH}`;
    }
    if (++count > UITREE_MAX_NODES) {
      return `${path}: exceeds max node count ${UITREE_MAX_NODES}`;
    }
    if (!isRecord(n)) {
      return `${path}: expected a node object`;
    }
    const type = n['type'];
    if (!oneOf(type, SUPPORTED_NODE_TYPES)) {
      return `${path}.type: unsupported "${String(type)}"`;
    }

    switch (type) {
      case 'stack': {
        if (!oneOf(n['direction'], ['vertical', 'horizontal'] as const)) {
          return `${path}.direction: expected "vertical" | "horizontal"`;
        }
        if (n['gap'] !== undefined && !oneOf(n['gap'], ['sm', 'md', 'lg'] as const)) {
          return `${path}.gap: invalid`;
        }
        return children(n['children'], depth, path);
      }
      case 'grid': {
        const columns = n['columns'];
        if (!isNumber(columns) || !Number.isInteger(columns) || columns < 1 || columns > 6) {
          return `${path}.columns: expected integer 1–6`;
        }
        if (n['gap'] !== undefined && !oneOf(n['gap'], ['sm', 'md', 'lg'] as const)) {
          return `${path}.gap: invalid`;
        }
        return children(n['children'], depth, path);
      }
      case 'heading': {
        if (![1, 2, 3, 4].includes(n['level'] as number)) {
          return `${path}.level: expected 1–4`;
        }
        return isString(n['text']) ? null : `${path}.text: expected string`;
      }
      case 'text':
        return isString(n['text']) ? null : `${path}.text: expected string`;
      case 'metric': {
        if (!isString(n['label'])) return `${path}.label: expected string`;
        if (!isScalar(n['value'])) return `${path}.value: expected string | number`;
        if (!isOptString(n['unit'])) return `${path}.unit: expected string`;
        if (n['trend'] !== undefined && !oneOf(n['trend'], ['up', 'down', 'flat'] as const)) {
          return `${path}.trend: invalid`;
        }
        return null;
      }
      case 'statGrid':
        return list(n['items'], `${path}.items`, (it, p) => {
          if (!isString(it['label'])) return `${p}.label: expected string`;
          if (!isScalar(it['value'])) return `${p}.value: expected string | number`;
          return isOptString(it['unit']) ? null : `${p}.unit: expected string`;
        });
      case 'table': {
        if (!isOptString(n['filterKey'])) return `${path}.filterKey: expected string`;
        const colErr = list(n['columns'], `${path}.columns`, (c, p) => {
          if (!isString(c['key'])) return `${p}.key: expected string`;
          if (!isString(c['label'])) return `${p}.label: expected string`;
          if (c['align'] !== undefined && !oneOf(c['align'], ['left', 'right', 'center'] as const)) {
            return `${p}.align: invalid`;
          }
          return null;
        });
        if (colErr) return colErr;
        const tableBound = n['dataSource'] !== undefined;
        if (tableBound) {
          if (!isString(n['dataSource'])) return `${path}.dataSource: expected string`;
          if (!isString(n['dataPath'])) return `${path}.dataPath: expected string`;
        }
        if (n['rows'] === undefined) {
          return tableBound ? null : `${path}.rows: expected array`;
        }
        return list(n['rows'], `${path}.rows`, (row, p) => {
          for (const [k, cell] of Object.entries(row)) {
            if (!(isScalar(cell) || typeof cell === 'boolean' || cell === null)) {
              return `${p}.${k}: expected string | number | boolean | null`;
            }
          }
          return null;
        });
      }
      case 'barChart':
      case 'lineChart': {
        if (!isOptString(n['xLabel'])) return `${path}.xLabel: expected string`;
        if (!isOptString(n['yLabel'])) return `${path}.yLabel: expected string`;
        const chartBound = n['dataSource'] !== undefined;
        if (chartBound) {
          if (!isString(n['dataSource'])) return `${path}.dataSource: expected string`;
          if (!isString(n['dataPath'])) return `${path}.dataPath: expected string`;
          if (!isString(n['xKey'])) return `${path}.xKey: expected string`;
          const skErr = list(n['seriesKeys'], `${path}.seriesKeys`, (sk, p) => {
            if (!isString(sk['label'])) return `${p}.label: expected string`;
            return isString(sk['yKey']) ? null : `${p}.yKey: expected string`;
          });
          if (skErr) return skErr;
        }
        if (n['series'] === undefined) {
          return chartBound ? null : `${path}.series: expected array`;
        }
        return list(n['series'], `${path}.series`, (s, p) => {
          if (!isString(s['label'])) return `${p}.label: expected string`;
          return list(s['points'], `${p}.points`, (pt, pp) => {
            if (!isScalar(pt['x'])) return `${pp}.x: expected string | number`;
            return isNumber(pt['y']) ? null : `${pp}.y: expected number`;
          });
        });
      }
      case 'list': {
        if (!isOptString(n['filterKey'])) return `${path}.filterKey: expected string`;
        return list(n['items'], `${path}.items`, (it, p) => {
          if (!isString(it['title'])) return `${p}.title: expected string`;
          if (!isOptString(it['subtitle'])) return `${p}.subtitle: expected string`;
          return it['value'] === undefined || isScalar(it['value']) ? null : `${p}.value: invalid`;
        });
      }
      case 'button': {
        if (!isString(n['label'])) return `${path}.label: expected string`;
        if (!isString(n['action'])) return `${path}.action: expected string`;
        if (!isOptRecord(n['params'])) return `${path}.params: expected object`;
        return isOptTarget(n['target']) ? null : `${path}.target: expected "replace" | "append"`;
      }
      case 'toggle': {
        if (!isString(n['label'])) return `${path}.label: expected string`;
        if (!isString(n['name'])) return `${path}.name: expected string`;
        if (!isString(n['action'])) return `${path}.action: expected string`;
        if (!isOptRecord(n['params'])) return `${path}.params: expected object`;
        return isOptTarget(n['target']) ? null : `${path}.target: expected "replace" | "append"`;
      }
      case 'filterBar': {
        if (!isString(n['filterKey'])) return `${path}.filterKey: expected string`;
        return isOptString(n['placeholder']) ? null : `${path}.placeholder: expected string`;
      }
      case 'download': {
        if (!isString(n['label'])) return `${path}.label: expected string`;
        return isString(n['url']) ? null : `${path}.url: expected string`;
      }
      case 'periodNav': {
        if (!isString(n['label'])) return `${path}.label: expected string`;
        if (!isString(n['prevAction'])) return `${path}.prevAction: expected string`;
        if (!isString(n['nextAction'])) return `${path}.nextAction: expected string`;
        if (!isOptRecord(n['prevParams'])) return `${path}.prevParams: expected object`;
        if (!isOptRecord(n['nextParams'])) return `${path}.nextParams: expected object`;
        return isOptTarget(n['target']) ? null : `${path}.target: expected "replace" | "append"`;
      }
      case 'form': {
        if (!isString(n['submitAction'])) return `${path}.submitAction: expected string`;
        if (!isOptString(n['submitLabel'])) return `${path}.submitLabel: expected string`;
        if (!isOptTarget(n['target'])) return `${path}.target: expected "replace" | "append"`;
        return list(n['fields'], `${path}.fields`, (f, p) => {
          if (!isString(f['name'])) return `${p}.name: expected string`;
          if (!isString(f['label'])) return `${p}.label: expected string`;
          if (!oneOf(f['type'], ['text', 'select'] as const)) return `${p}.type: expected "text" | "select"`;
          if (f['options'] !== undefined) {
            return list(f['options'], `${p}.options`, (o, op) => {
              if (!isString(o['label'])) return `${op}.label: expected string`;
              return isString(o['value']) ? null : `${op}.value: expected string`;
            });
          }
          return null;
        });
      }
    }
  };

  const children = (value_: unknown, depth: number, path: string): string | null => {
    if (!Array.isArray(value_)) {
      return `${path}.children: expected array`;
    }
    for (let i = 0; i < value_.length; i++) {
      const err = node(value_[i], depth + 1, `${path}.children[${i}]`);
      if (err) return err;
    }
    return null;
  };

  const list = (value_: unknown, path: string, each: (item: Record<string, unknown>, path: string) => string | null): string | null => {
    if (!Array.isArray(value_)) {
      return `${path}: expected array`;
    }
    for (let i = 0; i < value_.length; i++) {
      const item = value_[i];
      if (!isRecord(item)) return `${path}[${i}]: expected object`;
      const err = each(item, `${path}[${i}]`);
      if (err) return err;
    }
    return null;
  };

  const rootErr = node(value['root'], 0, 'root');
  return rootErr ? { ok: false, error: rootErr } : { ok: true, tree: value as unknown as UITree };
}

/** Parse helper: returns the validated tree or null (callers fall back to a text item on null). */
export function parseUITree(value: unknown): UITree | null {
  const result = validateUITree(value);
  return result.ok ? result.tree : null;
}

/** Boolean guard for convenience. */
export function isUITree(value: unknown): value is UITree {
  return validateUITree(value).ok;
}
